import type { AdminGroupPublic, UserPublic } from '@/client/types.gen';
import { adminRbacFacade } from '@/facades/systemAdmin';

const STORAGE_KEY = 'comic10k:state:admin:permission-preview:v1';
const PREVIEW_GROUP_HEADER = 'X-Admin-Preview-Group-Id';
const ADMIN_PROXY_PREFIXES = ['/admin/', '/admin-ap/', '/admin-us/'];

type PermissionPreviewState = {
  group_id: number;
};

const isBrowser = () => typeof window !== 'undefined';

const readStoredPreview = (): PermissionPreviewState | null => {
  if (!isBrowser()) {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<PermissionPreviewState> | null;
    const groupId = Number(parsed?.group_id);
    if (!Number.isFinite(groupId) || groupId <= 0) {
      return null;
    }
    return { group_id: Math.trunc(groupId) };
  } catch {
    return null;
  }
};

const writeStoredPreview = (value: PermissionPreviewState | null) => {
  if (!isBrowser()) {
    return;
  }
  if (!value) {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return;
  }
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
};

export const getPermissionPreviewGroupId = () => readStoredPreview()?.group_id || null;

export const setPermissionPreviewGroupId = (groupId: number | null) => {
  writeStoredPreview(groupId ? { group_id: groupId } : null);
};

export const clearPermissionPreview = () => {
  writeStoredPreview(null);
};

const shouldAttachPreviewHeader = (input: RequestInfo | URL) => {
  if (!isBrowser()) {
    return false;
  }
  const rawUrl = input instanceof Request ? input.url : String(input);
  try {
    const url = new URL(rawUrl, window.location.origin);
    return (
      url.origin === window.location.origin &&
      ADMIN_PROXY_PREFIXES.some((prefix) => url.pathname.startsWith(prefix))
    );
  } catch {
    return ADMIN_PROXY_PREFIXES.some((prefix) =>
      String(rawUrl).startsWith(prefix),
    );
  }
};

export const installPermissionPreviewFetchInterceptor = () => {
  if (!isBrowser()) {
    return;
  }
  const marker = '__comic10kPermissionPreviewFetchInstalled';
  const globalWindow = window as typeof window & { [marker]?: boolean };
  if (globalWindow[marker]) {
    return;
  }
  globalWindow[marker] = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const groupId = getPermissionPreviewGroupId();
    if (!groupId || !shouldAttachPreviewHeader(input)) {
      return nativeFetch(input, init);
    }

    const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
    headers.set(PREVIEW_GROUP_HEADER, String(groupId));
    return nativeFetch(input, {
      ...init,
      headers,
    });
  };
};

export const buildPreviewCurrentUser = (
  baseUser: UserPublic,
  previewGroup: AdminGroupPublic,
): UserPublic => {
  return {
    ...baseUser,
    is_superuser: false,
    groups: [
      {
        id: previewGroup.id,
        code: previewGroup.code,
        name: previewGroup.name,
      },
    ],
    permissions: [...new Set(previewGroup.permission_codes || [])],
  };
};

export const resolvePermissionPreview = async (baseUser?: UserPublic): Promise<UserPublic | undefined> => {
  if (!baseUser) {
    return undefined;
  }
  if (!baseUser.is_superuser) {
    return baseUser;
  }

  const groupId = getPermissionPreviewGroupId();
  if (!groupId) {
    return baseUser;
  }

  try {
    const group = await adminRbacFacade.getGroup(groupId);
    if (!group?.is_active) {
      clearPermissionPreview();
      return baseUser;
    }
    return buildPreviewCurrentUser(baseUser, group);
  } catch {
    clearPermissionPreview();
    return baseUser;
  }
};
